import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {
  IAddItemRule,
  IAutoOrderExpirationSettings,
  IAutoOrderOrderingSettings,
  ICopyStoreSettingsResult,
  IDisableStockItemRule,
  IEmailSettings,
  IEnableStockItemRule,
  IMultiplePriceChangeRulesStoreSettings,
  IOpenOrderSettings,
  IPriceBandingStoreSettings,
  IStockItemEditThresholds,
  IStoreOverrideSettings,
  ITableNavSettings,
  SettingsReferenceEnum,
  TLineColour,
  TLocaleDateFormat,
  TStockTag,
  TStoreDisplayOrder,
  TSupplierEmail,
} from '../models';
import {
  settingGlobalTagsSettings,
  settingSharedLineColours,
  settingStoreStockItemAddRule,
  settingStoreAOExpirationSettings,
  settingStoreAOStoreOpenOrderSettings,
  settingStoreAOStoreOrderScheduler,
  settingStoreAOSupplierEmails,
  settingStoreCopySettings,
  settingUserDateFormatting,
  settingUserEditPreviewColsNGPReport,
  settingUserEditPreviewColsStock,
  settingUserFitToGrid,
  settingUserStoreDisplayOrder,
  settingUserStoreMailerConfiguration,
  settingUserStoreOrderConfiguration,
  settingUserTablePreferences,
  settingsSetSelected,
  settingStoreMultiplePriceChangeRules,
  settingStorePriceBands,
  settingStoreStockEditThresholdRules,
  settingStockItemEnableRules,
  settingStoreOverrides,
  settingStoreProcessedOrder,
  settingStoreTagsSettings,
} from './setting.actions';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IProcessedOrderSettings} from '../models/auto-ordering/ao-processed-order-settings.interface';
import {IPriceBand} from '../../../shared/shared-models/price-banding/price-band';

export interface ISettingState {
  application: {
    fitToGrid: boolean;
    preferences: {
      dateFormat: TLocaleDateFormat;
      storeDisplayOrder: TStoreDisplayOrder;
      customStoreOrder: string[];
    },
    tableSettings: ITableNavSettings;
  },
  copyStoreSettingsResult: ICopyStoreSettingsResult | null;
  errors: IGlobalError[];
  feature: {
    autoOrder: {
      storeOrderExpirationSettings: {
        [storeId: string]: IAutoOrderExpirationSettings;
      },
      storeOpenOrderSettings: {
        [storeId: string]: IOpenOrderSettings;
      },
      storeOrderSettings: {
        [storeId: string]: IAutoOrderOrderingSettings;
      },
      storeSupplierEmails: {
        [storeId: string]: { [account: string]: TSupplierEmail[] };
      }
    },
    email: {
      microsoftMailBusyLabel: string | null;
      storeMailerSettings: {
        [storeId: string]: IAutoOrderOrderingSettings;
      },
      usersEmailSettings: {
        [storeId: string]: Record<string, IEmailSettings>;
      },
      usersMailerDocumentExists: {
        [storeId: string]: Record<string, boolean>;
      },
    },
    general: {
      overrides: {
        [storeId: string]: IStoreOverrideSettings;
      },
      priceEdits: {
        [storeId: string]: IProcessedOrderSettings;
      },
    },
    ngpReport: {
      editsPreview: { [index: number]: string }
    },
    shelfTalkers: {},
    stockRules: {
      globalStockTags: TStockTag[];
      lineColours: {
        [storeId: string]: TLineColour;
      },
      stockItemAdd: {
        [storeId: string]: IAddItemRule;
      },
      stockItemDisable: {
        [storeId: string]: IDisableStockItemRule;
      },
      stockItemEditThreshold: {
        [storeId: string]: IStockItemEditThresholds;
      },
      stockItemEnable: {
        [storeId: string]: IEnableStockItemRule;
      },
      stockTagsSetting: {
        [storeId: string]: TStockTag[];
      },
      globalPriceBands: IPriceBand[];
      priceBands: {
        [storeId: string]: IPriceBandingStoreSettings;
      },
      multiplePriceChangeRules: {
        [storeId: string]: IMultiplePriceChangeRulesStoreSettings;
      },
    },
    stockManager: {
      editsPreview: { [index: number]: string },
    }
  },
  isLoading: {
    addStockItemRules: boolean;
    aoExpirationSettings: boolean;
    aoSupplierEmails: boolean;
    copyStoreSettings: boolean;
    dateFormat: boolean;
    disableStockItemRules: boolean;
    storeDisplayOrder: boolean;
    editsPreviewNGPReport: boolean;
    editsPreviewStock: boolean;
    enableStockItemRules: boolean;
    fitToGrid: boolean;
    globalStockTags: boolean;
    lineColours: boolean;
    openOrderSettings: boolean;
    orderSettings: boolean;
    overrideSettings: boolean;
    processedOrderSettings: boolean;
    priceBands: boolean;
    priceBandsSave: boolean;
    multiplePriceChangeRules: boolean;
    multiplePriceChangeRulesSave: boolean;
    stockItemEditThreshold: boolean
    stockTagsSetting: boolean;
    tableSettings: boolean;
    userEmailSettings: boolean;
    submittingEmailSettings: boolean;
  },
  priceBandsSavedAt: {
    [storeId: string]: number;
  },
  selectedSection: string;
  selectedSettingNavigationRef: SettingsReferenceEnum;
  selectedSettingRef: SettingsReferenceEnum;
  selectedSubSection: string;
}

export const initialISettingState: Readonly<ISettingState> = {
  application: {
    fitToGrid: false,
    preferences: {
      dateFormat: 'FULL',
      storeDisplayOrder: 'Alphabetical',
      customStoreOrder: [],
    },
    tableSettings: undefined,
  },
  copyStoreSettingsResult: null,
  errors: [],
  feature: {
    autoOrder: {
      storeOrderExpirationSettings: {},
      storeOpenOrderSettings: {},
      storeOrderSettings: {},
      storeSupplierEmails: {},
    },
    email: {
      microsoftMailBusyLabel: null,
      storeMailerSettings: {},
      usersEmailSettings: {},
      usersMailerDocumentExists: {},
    },
    general: {
      overrides: {},
      priceEdits: {},
    },
    ngpReport: {
      editsPreview: undefined,
    },
    shelfTalkers: {},
    stockRules: {
      globalStockTags: [],
      lineColours: {},
      stockItemAdd: {},
      stockItemDisable: {},
      stockItemEditThreshold: {},
      stockItemEnable: {},
      stockTagsSetting: {},
      globalPriceBands: [],
      priceBands: {},
      multiplePriceChangeRules: {},
    },
    stockManager: {
      editsPreview: undefined,
    },
  },
  isLoading: {
    addStockItemRules: false,
    aoExpirationSettings: false,
    aoSupplierEmails: false,
    copyStoreSettings: false,
    dateFormat: false,
    disableStockItemRules: false,
    storeDisplayOrder: false,
    editsPreviewNGPReport: false,
    editsPreviewStock: false,
    enableStockItemRules: false,
    fitToGrid: false,
    globalStockTags: false,
    lineColours: false,
    openOrderSettings: false,
    orderSettings: false,
    overrideSettings: false,
    processedOrderSettings: false,
    priceBands: false,
    priceBandsSave: false,
    multiplePriceChangeRules: false,
    multiplePriceChangeRulesSave: false,
    stockItemEditThreshold: false,
    stockTagsSetting: false,
    tableSettings: false,
    userEmailSettings: false,
    submittingEmailSettings: false,
  },
  priceBandsSavedAt: {},
  selectedSection: 'Account',
  selectedSettingNavigationRef: SettingsReferenceEnum.accountUserProfile,
  selectedSettingRef: SettingsReferenceEnum.accountUserProfile,
  selectedSubSection: '',
};

const withMicrosoftMailBusy = (state: ISettingState, label: string): ISettingState => ({
  ...state,
  feature: {
    ...state.feature,
    email: {
      ...state.feature.email,
      microsoftMailBusyLabel: label,
    },
  },
});

const clearMicrosoftMailBusy = (state: ISettingState): ISettingState => ({
  ...state,
  feature: {
    ...state.feature,
    email: {
      ...state.feature.email,
      microsoftMailBusyLabel: null,
    },
  },
});

const withUserMailerSettings = (
  state: ISettingState,
  userId: string,
  storeId: string,
  mailSettings: IEmailSettings,
  mailerDocumentExists?: boolean,
): ISettingState => {
  const previousExists = state.feature.email.usersMailerDocumentExists?.[storeId]?.[userId] ?? false;
  const nextExists = mailerDocumentExists ?? previousExists;
  return {
    ...clearMicrosoftMailBusy(state),
    feature: {
      ...state.feature,
      email: {
        ...state.feature.email,
        microsoftMailBusyLabel: null,
        usersEmailSettings: {
          ...state.feature.email.usersEmailSettings,
          [storeId]: {
            ...(state.feature.email.usersEmailSettings?.[storeId] ?? {}),
            [userId]: mailSettings,
          },
        },
        usersMailerDocumentExists: {
          ...state.feature.email.usersMailerDocumentExists,
          [storeId]: {
            ...(state.feature.email.usersMailerDocumentExists?.[storeId] ?? {}),
            [userId]: nextExists,
          },
        },
      },
    },
  };
};

const createSettingReducer: ActionReducer<ISettingState> = createReducer(
  initialISettingState,
  //#region [Settings | User] Get Date Formatting **
  on(settingUserDateFormatting.getDateFormatting, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      dateFormat: true,
    },
  })),
  on(settingUserDateFormatting.getDateFormattingSuccess, (state, {format}) => ({
    ...state,
    application: {
      ...state.application,
      preferences: {
        ...state.application.preferences,
        dateFormat: format,
      },
    },
    isLoading: {
      ...state.isLoading,
      dateFormat: false,
    },
  })),
  on(settingUserDateFormatting.getDateFormattingFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      dateFormat: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Date Formatting **
  on(settingUserDateFormatting.setDateFormatting, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      dateFormat: true,
    },
  })),
  on(settingUserDateFormatting.setDateFormattingSuccess, (state, {format}) => ({
    ...state,
    application: {
      ...state.application,
      preferences: {
        ...state.application.preferences,
        dateFormat: format,
      },
    },
    isLoading: {
      ...state.isLoading,
      dateFormat: false,
    },
  })),
  on(settingUserDateFormatting.setDateFormattingFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      dateFormat: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Get Store Display Order **
  on(settingUserStoreDisplayOrder.getStoreDisplayOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: true,
    },
  })),
  on(settingUserStoreDisplayOrder.getStoreDisplayOrderSuccess, (state, {order, customStoreOrder}) => ({
    ...state,
    application: {
      ...state.application,
      preferences: {
        ...state.application.preferences,
        storeDisplayOrder: order,
        customStoreOrder: customStoreOrder ?? state.application.preferences.customStoreOrder,
      },
    },
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: false,
    },
  })),
  on(settingUserStoreDisplayOrder.getStoreDisplayOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Store Display Order **
  on(settingUserStoreDisplayOrder.setStoreDisplayOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: true,
    },
  })),
  on(settingUserStoreDisplayOrder.setStoreDisplayOrderSuccess, (state, {order, customStoreOrder}) => ({
    ...state,
    application: {
      ...state.application,
      preferences: {
        ...state.application.preferences,
        storeDisplayOrder: order,
        customStoreOrder: customStoreOrder ?? state.application.preferences.customStoreOrder,
      },
    },
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: false,
    },
  })),
  on(settingUserStoreDisplayOrder.setStoreDisplayOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      storeDisplayOrder: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Get Edit Preview Cols NGP Report **
  on(settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReport, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: true,
    },
  })),
  on(settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportSuccess, (state, {visibleFields}) => ({
    ...state,
    feature: {
      ...state.feature,
      ngpReport: {
        ...state.feature.ngpReport,
        editsPreview: visibleFields,
      },
    },
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: false,
    },
  })),
  on(settingUserEditPreviewColsNGPReport.getEditPreviewColsNGPReportFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Edit Preview Cols NGP Report **
  on(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReport, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: true,
    },
  })),
  on(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportSuccess, (state, {visibleFields}) => ({
    ...state,
    feature: {
      ...state.feature,
      ngpReport: {
        ...state.feature.ngpReport,
        editsPreview: visibleFields,
      },
    },
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: false,
    },
  })),
  on(settingUserEditPreviewColsNGPReport.setEditPreviewColsNGPReportFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      editsPreviewNGPReport: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Get Edit Preview Cols Stock **
  on(settingUserEditPreviewColsStock.getEditPreviewColsStock, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: true,
    },
  })),
  on(settingUserEditPreviewColsStock.getEditPreviewColsStockSuccess, (state, {visibleFields}) => ({
    ...state,
    feature: {
      ...state.feature,
      stockManager: {
        ...state.feature.stockManager,
        editsPreview: visibleFields,
      },
    },
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: false,
    },
  })),
  on(settingUserEditPreviewColsStock.getEditPreviewColsStockFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Edit Preview Cols Stock **
  on(settingUserEditPreviewColsStock.setEditPreviewColsStock, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: true,
    },
  })),
  on(settingUserEditPreviewColsStock.setEditPreviewColsStockSuccess, (state, {visibleFields}) => ({
    ...state,
    feature: {
      ...state.feature,
      stockManager: {
        ...state.feature.stockManager,
        editsPreview: visibleFields,
      },
    },
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: false,
    },
  })),
  on(settingUserEditPreviewColsStock.setEditPreviewColsStockFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      editsPreviewStock: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Get Fit To Grid **
  on(settingUserFitToGrid.getFitToGrid, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      fitToGrid: true,
    },
  })),
  on(settingUserFitToGrid.getFitToGridSuccess, (state, {fit}) => ({
    ...state,
    application: {
      ...state.application,
      fitToGrid: fit,
    },
    isLoading: {
      ...state.isLoading,
      fitToGrid: false,
    },
  })),
  on(settingUserFitToGrid.getFitToGridFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      fitToGrid: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Fit To Grid **
  on(settingUserFitToGrid.setFitToGrid, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      fitToGrid: true,
    },
  })),
  on(settingUserFitToGrid.setFitToGridSuccess, (state, {fit}) => ({
    ...state,
    application: {
      ...state.application,
      fitToGrid: fit,
    },
    isLoading: {
      ...state.isLoading,
      fitToGrid: false,
    },
  })),
  on(settingUserFitToGrid.setFitToGridFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      fitToGrid: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Get Table Preferences **
  on(settingUserTablePreferences.getTablePreferences, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      tableSettings: true,
    },
  })),
  on(settingUserTablePreferences.getTablePreferencesSuccess, (state, {settings}) => ({
    ...state,
    application: {
      ...state.application,
      tableSettings: settings,
    },
    isLoading: {
      ...state.isLoading,
      tableSettings: false,
    },
  })),
  on(settingUserTablePreferences.getTablePreferencesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      tableSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | User] Set Table Preferences **
  on(settingUserTablePreferences.setTablePreferences, (state, {settings}) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      tableSettings: true,
    },
  })),
  on(settingUserTablePreferences.setTablePreferencesSuccess, (state, {settings}) => ({
    ...state,
    application: {
      ...state.application,
      tableSettings: settings,
    },
    isLoading: {
      ...state.isLoading,
      tableSettings: false,
    },
  })),
  on(settingUserTablePreferences.setTablePreferencesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      tableSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | User | Store] Get Order Configuration **
  on(settingUserStoreOrderConfiguration.getOrderConfiguration, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      orderSettings: true,
    },
  })),
  on(settingUserStoreOrderConfiguration.getOrderConfigurationSuccess, (state, {orderSettings, store}) => ({
    ...state,
    feature: {
      ...state.feature,
      autoOrder: {
        ...state.feature.autoOrder,
        storeOrderSettings: {
          ...state.feature.autoOrder.storeOrderSettings,
          [store.storeId]: orderSettings,
        },
      },
    },
    isLoading: {
      ...state.isLoading,
      orderSettings: false,
    },
  })),
  on(settingUserStoreOrderConfiguration.getOrderConfigurationFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      orderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | User | Store] Set Order Configuration **
  on(settingUserStoreOrderConfiguration.setOrderConfiguration, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      orderSettings: true,
    },
  })),
  on(settingUserStoreOrderConfiguration.setOrderConfigurationSuccess, (state, {orderSettings, store}) => {
    const patch: Partial<IAutoOrderOrderingSettings> = {
      ...(orderSettings?.body && {body: orderSettings.body}),
      ...(orderSettings?.ccRecipients && {ccRecipients: orderSettings.ccRecipients}),
      ...(orderSettings?.contactName && {contactName: orderSettings.contactName}),
      ...(orderSettings?.contactNumber && {contactNumber: orderSettings.contactNumber}),
      ...(orderSettings?.orderNumber && {orderNumber: orderSettings.orderNumber}),
      ...(orderSettings?.pdfClause && {pdfClause: orderSettings.pdfClause}),
      ...(orderSettings?.subject && {subject: orderSettings.subject}),
    };

    return {
      ...state,
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOrderSettings: {
            ...state.feature.autoOrder.storeOrderSettings,
            [store.storeId]: {
              ...state.feature.autoOrder.storeOrderSettings[store.storeId],
              ...patch,
            },
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        orderSettings: false,
      },
    };
  }),
  on(settingUserStoreOrderConfiguration.setOrderConfigurationFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      orderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Stock Item Add Rules **
  on(settingStoreStockItemAddRule.getAddItemRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      addStockItemRules: true,
    },
  })),
  on(settingStoreStockItemAddRule.getAddItemRulesSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const stockItemAdd = {...state.feature.stockRules.stockItemAdd};
    Object.entries(settings).forEach(([storeId, setting]) => {
      stockItemAdd[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemAdd,
        },
      },
      isLoading: {
        ...state.isLoading,
        addStockItemRules: false,
      },
    };
  }),
  on(settingStoreStockItemAddRule.getAddItemRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      addStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Add Item Rules **
  on(settingStoreStockItemAddRule.setAddItemRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      addStockItemRules: true,
    },
  })),
  on(settingStoreStockItemAddRule.setAddItemRulesSuccess, (state, {rules, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemAdd: {
            ...state.feature.stockRules.stockItemAdd,
            [store.storeId]: rules,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        addStockItemRules: false,
      },
    };
  }),
  on(settingStoreStockItemAddRule.setAddItemRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      addStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get AO Supplier Emails **
  on(settingStoreAOSupplierEmails.getAutoOrderSupplierEmails, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      aoSupplierEmails: true,
    },
  })),
  on(settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const storeSupplierEmails = {...state.feature.autoOrder.storeSupplierEmails};
    Object.entries(settings).forEach(([storeId, setting]) => {
      storeSupplierEmails[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeSupplierEmails,
        },
      },
      isLoading: {
        ...state.isLoading,
        aoSupplierEmails: false,
      },
    };
  }),
  on(settingStoreAOSupplierEmails.getAutoOrderSupplierEmailsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      aoSupplierEmails: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set AO Supplier Emails **
  on(settingStoreAOSupplierEmails.setAutoOrderSupplierEmails, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      aoSupplierEmails: true,
    },
  })),
  on(settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsSuccess, (state, {emails, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeSupplierEmails: {
            ...state.feature.autoOrder.storeSupplierEmails,
            [store.storeId]: emails,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        aoSupplierEmails: false,
      },
    };
  }),
  on(settingStoreAOSupplierEmails.setAutoOrderSupplierEmailsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      aoSupplierEmails: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get AO Expiration **
  on(settingStoreAOExpirationSettings.getAOExpiration, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      aoExpirationSettings: true,
    },
  })),
  on(settingStoreAOExpirationSettings.getAOExpirationSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const storeOrderExpirationSettings = {...state.feature.autoOrder.storeOrderExpirationSettings};
    Object.entries(settings).forEach(([storeId, setting]) => {
      storeOrderExpirationSettings[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOrderExpirationSettings,
        },
      },
      isLoading: {
        ...state.isLoading,
        aoExpirationSettings: false,
      },
    };
  }),
  on(settingStoreAOExpirationSettings.getAOExpirationFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      aoExpirationSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set AO Expiration **
  on(settingStoreAOExpirationSettings.setAOExpiration, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      aoExpirationSettings: true,
    },
  })),
  on(settingStoreAOExpirationSettings.setAOExpirationSuccess, (state, {settings, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOrderExpirationSettings: {
            ...state.feature.autoOrder.storeOrderExpirationSettings,
            [store.storeId]: settings,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        aoExpirationSettings: false,
      },
    };
  }),
  on(settingStoreAOExpirationSettings.setAOExpirationFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      aoExpirationSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Enable Stock Item Rule **
  on(settingStockItemEnableRules.getEnableStockItemRule, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      enableStockItemRules: true,
    },
  })),
  on(settingStockItemEnableRules.getEnableStockItemRuleSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const stockItemEnable = {...state.feature.stockRules.stockItemEnable};
    Object.entries(settings).forEach(([storeId, setting]) => {
      stockItemEnable[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemEnable,
        },
      },
      isLoading: {
        ...state.isLoading,
        enableStockItemRules: false,
      },
    };
  }),
  on(settingStockItemEnableRules.getEnableStockItemRuleFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      enableStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Enable Stock Item Rule **
  on(settingStockItemEnableRules.setEnableStockItemRule, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      enableStockItemRules: true,
    },
  })),
  on(settingStockItemEnableRules.setEnableStockItemRuleSuccess, (state, {settings, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemEnable: {
            ...state.feature.stockRules.stockItemEnable,
            [store.storeId]: settings,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        enableStockItemRules: false,
      },
    };
  }),
  on(settingStockItemEnableRules.setEnableStockItemRuleFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      enableStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Disable Stock Item Rule **
  on(settingStockItemEnableRules.getDisableStockItemRule, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      disableStockItemRules: true,
    },
  })),
  on(settingStockItemEnableRules.getDisableStockItemRuleSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const stockItemDisable = {...state.feature.stockRules.stockItemDisable};
    Object.entries(settings).forEach(([storeId, setting]) => {
      stockItemDisable[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemDisable,
        },
      },
      isLoading: {
        ...state.isLoading,
        disableStockItemRules: false,
      },
    };
  }),
  on(settingStockItemEnableRules.getDisableStockItemRuleFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      disableStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Disable Stock Item Rule **
  on(settingStockItemEnableRules.setDisableStockItemRule, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      disableStockItemRules: true,
    },
  })),
  on(settingStockItemEnableRules.setDisableStockItemRuleSuccess, (state, {settings, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemDisable: {
            ...state.feature.stockRules.stockItemDisable,
            [store.storeId]: settings,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        disableStockItemRules: false,
      },
    };
  }),
  on(settingStockItemEnableRules.setDisableStockItemRuleFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      disableStockItemRules: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get AO Full Open Order **
  on(settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      openOrderSettings: true,
    },
  })),
  on(settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const storeOpenOrderSettings = {...state.feature.autoOrder.storeOpenOrderSettings};
    Object.entries(settings).forEach(([storeId, setting]) => {
      storeOpenOrderSettings[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOpenOrderSettings,
        },
      },
      isLoading: {
        ...state.isLoading,
        openOrderSettings: false,
      },
    };
  }),
  on(settingStoreAOStoreOpenOrderSettings.getAOFullOpenOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      openOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set AO Full Open Order **
  on(settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      openOrderSettings: true,
    },
  })),
  on(settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderSuccess, (state, {settings, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOpenOrderSettings: {
            ...state.feature.autoOrder.storeOpenOrderSettings,
            [store.storeId]: settings,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        openOrderSettings: false,
      },
    };
  }),
  on(settingStoreAOStoreOpenOrderSettings.setAOFullOpenOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      openOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get / Set AO Order Scheduler (Account Settings) **
  on(settingStoreAOStoreOrderScheduler.getAOOrderScheduler, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      openOrderSettings: true,
    },
  })),
  on(settingStoreAOStoreOrderScheduler.getAOOrderSchedulerSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const storeOpenOrderSettings = {...state.feature.autoOrder.storeOpenOrderSettings};
    Object.entries(settings).forEach(([storeId, accountSettings]) => {
      if (storeOpenOrderSettings[storeId]) {
        storeOpenOrderSettings[storeId] = {
          ...storeOpenOrderSettings[storeId],
          orderAccountSettings: accountSettings,
        };
      } else {
        storeOpenOrderSettings[storeId] = {
          orderAccountSettings: accountSettings,
        } as IOpenOrderSettings;
      }
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOpenOrderSettings,
        },
      },
      isLoading: {
        ...state.isLoading,
        openOrderSettings: false,
      },
    };
  }),
  on(settingStoreAOStoreOrderScheduler.getAOOrderSchedulerFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      openOrderSettings: false,
    },
  })),
  on(settingStoreAOStoreOrderScheduler.setAOOrderScheduler, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      openOrderSettings: true,
    },
  })),
  on(settingStoreAOStoreOrderScheduler.setAOOrderSchedulerSuccess, (state, {settings, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        autoOrder: {
          ...state.feature.autoOrder,
          storeOpenOrderSettings: {
            ...state.feature.autoOrder.storeOpenOrderSettings,
            [store.storeId]: {
              ...state.feature.autoOrder.storeOpenOrderSettings[store.storeId],
              orderAccountSettings: settings,
            },
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        openOrderSettings: false,
      },
    };
  }),
  on(settingStoreAOStoreOrderScheduler.setAOOrderSchedulerFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      openOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Store Override **
  on(settingStoreOverrides.getStoreOverride, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      overrideSettings: true,
    },
  })),
  on(settingStoreOverrides.getStoreOverrideSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const overrides = {...state.feature.general.overrides};
    Object.entries(settings).forEach(([storeId, setting]) => {
      overrides[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        general: {
          ...state.feature.general,
          overrides,
        },
      },
      isLoading: {
        ...state.isLoading,
        overrideSettings: false,
      },
    };
  }),
  on(settingStoreOverrides.getStoreOverrideFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      overrideSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Store Override **
  on(settingStoreOverrides.setStoreOverride, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      openOrderSettings: true,
    },
  })),
  on(settingStoreOverrides.setStoreOverrideSuccess, (state, {overrides, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        general: {
          ...state.feature.general,
          overrides: {
            ...state.feature.general.overrides,
            [store.storeId]: overrides,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        openOrderSettings: false,
      },
    };
  }),
  on(settingStoreOverrides.setStoreOverrideFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      openOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Store Processed Order **
  on(settingStoreProcessedOrder.getStoreProcessedOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      processedOrderSettings: true,
    },
  })),
  on(settingStoreProcessedOrder.getStoreProcessedOrderSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const priceEdits = {...state.feature.general.priceEdits};
    Object.entries(settings).forEach(([storeId, setting]) => {
      priceEdits[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        general: {
          ...state.feature.general,
          priceEdits,
        },
      },
      isLoading: {
        ...state.isLoading,
        processedOrderSettings: false,
      },
    };
  }),
  on(settingStoreProcessedOrder.getStoreProcessedOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      processedOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Store Processed Order **
  on(settingStoreProcessedOrder.setStoreProcessedOrder, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      processedOrderSettings: true,
    },
  })),
  on(settingStoreProcessedOrder.setStoreProcessedOrderSuccess, (state, {processedOrder, store}) => ({
    ...state,
    feature: {
      ...state.feature,
      general: {
        ...state.feature.general,
        priceEdits: {
          ...state.feature.general.priceEdits,
          [store.storeId]: processedOrder,
        },
      },
    },
    isLoading: {
      ...state.isLoading,
      processedOrderSettings: false,
    },
  })),
  on(settingStoreProcessedOrder.setStoreProcessedOrderFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      processedOrderSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Stock Edit Threshold Rules **
  on(settingStoreStockEditThresholdRules.getStockEditThresholdRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      stockItemEditThreshold: true,
    },
  })),
  on(settingStoreStockEditThresholdRules.getStockEditThresholdRulesSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const stockItemEditThreshold = {...state.feature.stockRules.stockItemEditThreshold};
    Object.entries(settings).forEach(([storeId, setting]) => {
      stockItemEditThreshold[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemEditThreshold,
        },
      },
      isLoading: {
        ...state.isLoading,
        stockItemEditThreshold: false,
      },
    };
  }),
  on(settingStoreStockEditThresholdRules.getStockEditThresholdRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      stockItemEditThreshold: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Stock Edit Threshold Rules **
  on(settingStoreStockEditThresholdRules.setStockEditThresholdRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      stockItemEditThreshold: true,
    },
  })),
  on(settingStoreStockEditThresholdRules.setStockEditThresholdRulesSuccess, (state, {rules, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockItemEditThreshold: {
            ...state.feature.stockRules.stockItemEditThreshold,
            [store.storeId]: rules,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        stockItemEditThreshold: false,
      },
    };
  }),
  on(settingStoreStockEditThresholdRules.setStockEditThresholdRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      stockItemEditThreshold: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get Price Bands **
  on(settingStorePriceBands.getPriceBands, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      priceBands: true,
    },
  })),
  on(settingStorePriceBands.getPriceBandsSuccess, (state, {params, errors, fetchStartedAt, globalBands}) => {
    const {settings} = params;
    const priceBands = {...state.feature.stockRules.priceBands};
    Object.entries(settings ?? {}).forEach(([storeId, setting]) => {
      const savedAt: number = state.priceBandsSavedAt[storeId] ?? 0;
      const isStaleFetch: boolean = fetchStartedAt !== undefined && savedAt > fetchStartedAt;
      if (isStaleFetch) {
        return;
      }
      priceBands[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          globalPriceBands: globalBands,
          priceBands,
        },
      },
      isLoading: {
        ...state.isLoading,
        priceBands: false,
      },
    };
  }),
  on(settingStorePriceBands.getPriceBandsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      priceBands: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Price Bands **
  on(settingStorePriceBands.setPriceBands, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      priceBands: true,
      priceBandsSave: true,
    },
  })),
  on(settingStorePriceBands.setPriceBandsSuccess, (state, {bands, savedAt, source, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          priceBands: {
            ...state.feature.stockRules.priceBands,
            [store.storeId]: {
              bands,
              source,
            },
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        priceBands: false,
        priceBandsSave: false,
      },
      priceBandsSavedAt: {
        ...state.priceBandsSavedAt,
        [store.storeId]: savedAt ?? 0,
      },
    };
  }),
  on(settingStorePriceBands.setPriceBandsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      priceBands: false,
      priceBandsSave: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Get / Set Multiple Price Change Rules **
  on(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: true,
    },
  })),
  on(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRulesSuccess, (state, {params, errors}) => ({
    ...state,
    errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
    feature: {
      ...state.feature,
      stockRules: {
        ...state.feature.stockRules,
        multiplePriceChangeRules: {
          ...state.feature.stockRules.multiplePriceChangeRules,
          ...(params.settings ?? {}),
        },
      },
    },
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: false,
    },
  })),
  on(settingStoreMultiplePriceChangeRules.getMultiplePriceChangeRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: false,
    },
  })),
  on(settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRules, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: true,
      multiplePriceChangeRulesSave: true,
    },
  })),
  on(settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRulesSuccess, (state, {settings, store}) => ({
    ...state,
    feature: {
      ...state.feature,
      stockRules: {
        ...state.feature.stockRules,
        multiplePriceChangeRules: {
          ...state.feature.stockRules.multiplePriceChangeRules,
          [store.storeId]: settings,
        },
      },
    },
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: false,
      multiplePriceChangeRulesSave: false,
    },
  })),
  on(settingStoreMultiplePriceChangeRules.setMultiplePriceChangeRulesFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      multiplePriceChangeRules: false,
      multiplePriceChangeRulesSave: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Copy Store Settings **
  on(settingStoreCopySettings.copyStoreSettings, (state) => ({
    ...state,
    copyStoreSettingsResult: null,
    isLoading: {
      ...state.isLoading,
      copyStoreSettings: true,
    },
  })),
  on(settingStoreCopySettings.copyStoreSettingsSuccess, (state, {priceBands, savedAt, settings, targetStore}) => {
    const storePriceBands = {...state.feature.stockRules.priceBands};
    const priceBandsSavedAt = {...state.priceBandsSavedAt};

    if (priceBands) {
      storePriceBands[targetStore.storeId] = {
        bands: priceBands,
        source: 'store',
      };
      priceBandsSavedAt[targetStore.storeId] = savedAt ?? 0;
    }

    return {
      ...state,
      copyStoreSettingsResult: {
        settings,
        succeeded: true,
        targetStoreName: targetStore?.name ?? '',
      },
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          priceBands: storePriceBands,
        },
      },
      isLoading: {
        ...state.isLoading,
        copyStoreSettings: false,
      },
      priceBandsSavedAt,
    };
  }),
  on(settingStoreCopySettings.copyStoreSettingsFailure, (state, {error, settings, targetStore}) => ({
    ...state,
    copyStoreSettingsResult: {
      settings,
      succeeded: false,
      targetStoreName: targetStore?.name ?? '',
    },
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      copyStoreSettings: false,
    },
  })),
  on(settingStoreCopySettings.clearCopyStoreSettingsResult, (state) => ({
    ...state,
    copyStoreSettingsResult: null,
  })),
  //#endregion
  //#region [Settings | Store] Get Store Tags **
  on(settingStoreTagsSettings.getStoreTags, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      stockTagsSetting: true,
    },
  })),
  on(settingStoreTagsSettings.getStoreTagsSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const stockTagsSetting = {...state.feature.stockRules.stockTagsSetting};
    Object.entries(settings).forEach(([storeId, setting]) => {
      stockTagsSetting[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockTagsSetting,
        },
      },
      isLoading: {
        ...state.isLoading,
        stockTagsSetting: false,
      },
    };
  }),
  on(settingStoreTagsSettings.getStoreTagsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      stockTagsSetting: false,
    },
  })),
  //#endregion
  //#region [Settings | Store] Set Store Tags **
  on(settingStoreTagsSettings.setStoreTags, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      stockTagsSetting: true,
    },
  })),
  on(settingStoreTagsSettings.setStoreTagsSuccess, (state, {tags, store}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          stockTagsSetting: {
            ...state.feature.stockRules.stockTagsSetting,
            [store.storeId]: tags,
          },
        },
      },
      isLoading: {
        ...state.isLoading,
        stockTagsSetting: false,
      },
    };
  }),
  on(settingStoreTagsSettings.setStoreTagsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      stockTagsSetting: false,
    },
  })),
  //#endregion
  //#region [Settings | Global] Get Global Stock Tags **
  on(settingGlobalTagsSettings.getGlobalTags, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      globalStockTags: true,
    },
  })),
  on(settingGlobalTagsSettings.getGlobalTagsSuccess, (state, {tags}) => {
    return {
      ...state,
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          globalStockTags: tags,
        },
      },
      isLoading: {
        ...state.isLoading,
        globalStockTags: false,
      },
    };
  }),
  on(settingGlobalTagsSettings.getGlobalTagsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      globalStockTags: false,
    },
  })),
  //#endregion
  //#region [Settings | Shared] Get Line Colours **
  on(settingSharedLineColours.getLineColours, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      lineColours: true,
    },
  })),
  on(settingSharedLineColours.getLineColoursSuccess, (state, {params, errors}) => {
    const {settings} = params;
    const lineColours = {...state.feature.stockRules.lineColours};
    Object.entries(settings).forEach(([storeId, setting]) => {
      lineColours[storeId] = setting;
    });
    return {
      ...state,
      errors: errors?.length > 0 ? [...state.errors, ...errors] : [...state.errors],
      feature: {
        ...state.feature,
        stockRules: {
          ...state.feature.stockRules,
          lineColours,
        },
      },
      isLoading: {
        ...state.isLoading,
        lineColours: false,
      },
    };
  }),
  on(settingSharedLineColours.getLineColoursFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      lineColours: false,
    },
  })),
  //#endregion
  //#region [Settings | Selected] Set Selected **
  on(settingsSetSelected.setSelectedReference, (state, {ref}) => ({
    ...state,
    selectedSettingRef: ref,
  })),
  on(settingsSetSelected.setSelectedReferenceForNavigation, (state, {ref}) => ({
    ...state,
    selectedSettingNavigationRef: ref,
  })),
  on(settingsSetSelected.setSelectedSection, (state, {ref}) => ({
    ...state,
    selectedSection: ref ?? '',
  })),
  on(settingsSetSelected.setSelectedSubSection, (state, {ref}) => ({
    ...state,
    selectedSubSection: ref ?? '',
  })),
  on(settingsSetSelected.setSelectedSectionAndReference, (state, {ref, section}) => ({
    ...state,
    selectedSettingRef: ref,
    selectedSection: section ?? '',
  })),
  //#endregion
  //#region [Settings | User Store] Get All Users Mailer Configurations
  on(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurations, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      userEmailSettings: true,
    },
  })),
  on(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurationsSuccess, (state, {usersEmailSettings, usersMailerDocumentExists, store}) => ({
    ...state,
    feature: {
      ...state.feature,
      email: {
        ...state.feature.email,
        usersEmailSettings: {
          ...state.feature.email.usersEmailSettings,
          [store.storeId]: usersEmailSettings,
        },
        usersMailerDocumentExists: {
          ...state.feature.email.usersMailerDocumentExists,
          [store.storeId]: usersMailerDocumentExists,
        },
      },
    },
    isLoading: {
      ...state.isLoading,
      userEmailSettings: false,
    },
  })),
  on(settingUserStoreMailerConfiguration.getAllUsersMailerConfigurationsFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      userEmailSettings: false,
    },
  })),
  //#endregion
  //#region [Settings | User Store] Test Mailer Configuration
  on(settingUserStoreMailerConfiguration.testMailerConfiguration, (state) => ({
    ...state,
    isLoading: {
      ...state.isLoading,
      submittingEmailSettings: true,
    },
  })),
  on(settingUserStoreMailerConfiguration.testMailerConfigurationSuccess, (state, action) => {
    const current = state.feature.email.usersEmailSettings?.[action.storeId]?.[action.userId];
    const cleared: ISettingState = {
      ...state,
      isLoading: {
        ...state.isLoading,
        submittingEmailSettings: false,
      },
    };
    if (!current) {
      return cleared;
    }
    return withUserMailerSettings(cleared, action.userId, action.storeId, {
      ...current,
      lastTestAt: action.lastTestAt,
      lastTestAuthType: action.lastTestAuthType,
      lastTestMessage: action.lastTestMessage,
      lastTestStatus: action.status,
      status: action.status,
    }, true);
  }),
  on(settingUserStoreMailerConfiguration.testMailerConfigurationFailure, (state, {error}) => ({
    ...state,
    errors: [...state.errors, error],
    isLoading: {
      ...state.isLoading,
      submittingEmailSettings: false,
    },
  })),
  on(settingUserStoreMailerConfiguration.connectMicrosoftMail, (state) =>
    withMicrosoftMailBusy(state, 'Connecting Microsoft 365…'),
  ),
  on(settingUserStoreMailerConfiguration.disconnectMicrosoftMail, (state) =>
    withMicrosoftMailBusy(state, 'Disconnecting Microsoft 365…'),
  ),
  on(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsent, (state) =>
    withMicrosoftMailBusy(state, 'Opening Microsoft 365 admin approval…'),
  ),
  on(settingUserStoreMailerConfiguration.setMailerAuthTypeSuccess, (state, {userId, storeId, mailSettings, mailerDocumentExists}) =>
    withUserMailerSettings(state, userId, storeId, mailSettings, mailerDocumentExists),
  ),
  on(settingUserStoreMailerConfiguration.connectMicrosoftMailSuccess, (state, {userId, storeId, mailSettings, mailerDocumentExists}) =>
    withUserMailerSettings(state, userId, storeId, mailSettings, mailerDocumentExists),
  ),
  on(settingUserStoreMailerConfiguration.disconnectMicrosoftMailSuccess, (state, {userId, storeId, mailSettings, mailerDocumentExists}) =>
    withUserMailerSettings(state, userId, storeId, mailSettings, mailerDocumentExists),
  ),
  on(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentSuccess, (state) =>
    clearMicrosoftMailBusy(state),
  ),
  on(settingUserStoreMailerConfiguration.microsoftMailCancelled, (state) =>
    clearMicrosoftMailBusy(state),
  ),
  on(settingUserStoreMailerConfiguration.setMailerAuthTypeFailure, (state, {error}) => ({
    ...clearMicrosoftMailBusy(state),
    errors: [...state.errors, error],
  })),
  on(settingUserStoreMailerConfiguration.connectMicrosoftMailFailure, (state, {error}) => ({
    ...clearMicrosoftMailBusy(state),
    errors: [...state.errors, error],
  })),
  on(settingUserStoreMailerConfiguration.disconnectMicrosoftMailFailure, (state, {error}) => ({
    ...clearMicrosoftMailBusy(state),
    errors: [...state.errors, error],
  })),
  on(settingUserStoreMailerConfiguration.requestMicrosoftAdminConsentFailure, (state, {error}) => ({
    ...clearMicrosoftMailBusy(state),
    errors: [...state.errors, error],
  })),
  //#endregion
);

export const settingReducer = (state: ISettingState, action: Action): ISettingState => createSettingReducer(state, action);
